/**
 * `date` — centred `MMM D` / `MMM D, YYYY`, plus `HH:MM` when a time is set (M2 §5.6, §6).
 *
 * `value` is UTC; the **cell** renders in the viewer's timezone while `toText` (the API/CSV
 * form) stays UTC `[B5§10 case 13]`. Overdue dates render in `--negative-color` (ASSUMED, Q69).
 */
import { getColumnType, wallClockIn, type DateValue } from '@elizmonday/shared';
import { useState } from 'react';
import { CellPopover } from '../primitives';
import { defineColumnUi } from '../types';
import { sharedBuckets } from '../buckets';
import { DateSettingsDialog } from './SettingsDialog';

const shared = getColumnType('date');

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Measured: `.ds-date-picker-content` is 280 px wide `[editors/date/editor.json]`. */
export const DATE_PICKER_WIDTH = 280;

/**
 * **P-316 — monday's `Today` shortcut, as the round-11l capture found it.**
 *
 * The card guessed the picker's *footer* carried `Today` beside `Clear`. It does not, and the
 * capture is what we build from (`discovery/ui-reference/columns/date/11l-date-picker.json`,
 * `11l-date-picker-header.json`, sandbox board 18430299563, read-only):
 *
 *  - `Today` is the **first** control in the picker — a 56 × 32 button at the top-left of a
 *    264 × 32 `header.datePickerHeader` sitting on the popover's 8 px inset (the popover is
 *    280 × 429, so the header runs from the inner left edge to the inner right edge);
 *  - it is monday's **secondary** button kind at **small** size: 14 px / 400, `rgb(50,51,56)`
 *    on transparent, `1px solid rgb(195,198,212)`, radius 4, padding `4px 8px`;
 *  - its accessible name is exactly `Today`, it carries `tabIndex 0` and it is **not** disabled
 *    while the grid already shows the current month;
 *  - the only other thing in that row is a 32 × 32 `Add time` icon-button at the **right**
 *    (ours keeps `Add time` as the checkbox under the grid — a pre-existing divergence this
 *    card does not own);
 *  - monday's picker has **no `Clear` at all**: the row under the grid is an AI `Autofill date`
 *    (121 × 32). Ours keeps its `Clear`, which is likewise not this card's to remove.
 *
 * Whether monday's `Today` *also closes* the popover cannot be read without clicking it, and a
 * click writes a cell value — which the capture pass is not allowed to do. So it behaves exactly
 * like clicking today's day cell does here: it commits and the editor closes (**ASSUMED**, q597).
 */
export const DATE_PICKER_TODAY_BOX = { w: 56, h: 32 } as const;

const pad2 = (value: number): string => String(value).padStart(2, '0');

/**
 * Today's calendar date in `timezone` — what `Today` writes.
 *
 * A `date` cell holds a calendar date, not an instant, so "today" is only defined against a
 * zone: at 23:30 in Madrid it is already tomorrow in Kiritimati and still today in UTC. The
 * board screen supplies the zone the whole grid renders in (`CellRenderContext.timezone`), so
 * the shortcut agrees with the cell it is about to paint instead of with the server's UTC clock
 * `[B5§10 case 13]`. An absent zone means UTC, as everywhere else in the date pipeline.
 */
export function todayInTimeZone(timezone?: string, now: Date = new Date()): string {
  const clock = wallClockIn(now, timezone);
  return `${String(clock.year)}-${pad2(clock.month)}-${pad2(clock.day)}`;
}

export function formatDateCell(
  value: DateValue | null,
  settings: { show_current_year?: boolean },
  now: Date = new Date(),
): string {
  if (!value) return '';
  const [year, month, day] = value.date.split('-').map(Number) as [number, number, number];
  const showYear = settings.show_current_year === true || year !== now.getUTCFullYear();
  const head = `${MONTHS[month - 1]} ${day}${showYear ? `, ${year}` : ''}`;
  if (!value.time) return head;
  return `${head} ${value.time.slice(0, 5)}`;
}

/** ASSUMED (Q69): a date strictly before today, with no time, counts as overdue. */
export function isOverdue(value: DateValue | null, now: Date = new Date()): boolean {
  if (!value) return false;
  return value.date < now.toISOString().slice(0, 10);
}

function daysOfMonth(year: number, month: number): (number | null)[] {
  const first = new Date(Date.UTC(year, month, 1));
  const lead = (first.getUTCDay() + 6) % 7; // monday-first, as monday.com renders it
  const count = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const cells: (number | null)[] = Array.from({ length: lead }, () => null);
  for (let day = 1; day <= count; day += 1) cells.push(day);
  return cells;
}

export const dateColumnUi = defineColumnUi<DateValue, Record<string, unknown>>({
  type: 'date',
  icon: 'columnDate',
  gallery: { group: 'dates', keywords: ['date', 'due', 'deadline', 'calendar'] },
  /** P-022: `6054207833` `Due Date` (`width: null`) renders **140** px in monday. */
  defaultWidth: 140,
  align: 'center',
  groupable: true,
  /** P-263: the value is item-independent, so it may be written to a whole selection. */
  massEditable: true,

  Cell: ({ value, settings }) => (
    <span className="ez-cell__text" data-align="center" data-overdue={isOverdue(value) || undefined}>
      {formatDateCell(value, settings as { show_current_year?: boolean })}
    </span>
  ),

  Editor: ({ value, settings, context, onCommit, onCancel }) => {
    const today = new Date();
    const initial = value ? new Date(`${value.date}T00:00:00Z`) : today;
    const [cursor, setCursor] = useState({ year: initial.getUTCFullYear(), month: initial.getUTCMonth() });
    const [time, setTime] = useState(value?.time?.slice(0, 5) ?? '');
    const showTime = (settings as { show_time_by_default?: boolean }).show_time_by_default === true;
    const [withTime, setWithTime] = useState(Boolean(value?.time) || showTime);

    /** One commit path for every way a date is chosen: a day cell, `Today`, the typed field. */
    const commitDate = (date: string) => {
      onCommit({ date, ...(withTime && time ? { time: `${time}:00` } : {}) } as DateValue);
    };

    const iso = (day: number) =>
      `${cursor.year}-${String(cursor.month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

    const pick = (day: number) => commitDate(iso(day));

    return (
      /*
       * Measured on monday (`discovery/ui-reference/editors/date/editor.json`): the popover is
       * 280 x 429, white, radius 4, 8 px inset; a 264 x 32 header, a 264 x 32 date+time input
       * above the calendar, a 268 x 292 day grid and a 264 x 45 footer.
       */
      <CellPopover label="Pick a date" width={DATE_PICKER_WIDTH} onDismiss={onCancel}>
        <div className="ez-calendar">
          {/*
            * P-316: monday's `header.datePickerHeader` — the picker's **first** row, 264 × 32,
            * with `Today` at its left edge (`DATE_PICKER_TODAY_BOX`, and the comment above it for
            * the whole capture). It is the first focusable thing in the popover, so `Tab` from the
            * cell reaches it without a keyboard handler of its own.
            */}
          <div className="ez-calendar__header">
            <button
              type="button"
              className="ez-calendar__today"
              aria-label="Today"
              data-testid="date-today"
              onClick={() => commitDate(todayInTimeZone(context.timezone))}
            >
              Today
            </button>
          </div>
          <div className="ez-calendar__head">
            <button
              type="button"
              aria-label="Previous month"
              data-testid="calendar-prev"
              onClick={() =>
                setCursor(({ year, month }) =>
                  month === 0 ? { year: year - 1, month: 11 } : { year, month: month - 1 },
                )
              }
            >
              ‹
            </button>
            <span>{`${MONTHS[cursor.month]} ${cursor.year}`}</span>
            <button
              type="button"
              aria-label="Next month"
              data-testid="calendar-next"
              onClick={() =>
                setCursor(({ year, month }) =>
                  month === 11 ? { year: year + 1, month: 0 } : { year, month: month + 1 },
                )
              }
            >
              ›
            </button>
          </div>
          {/* monday puts a typed date (and time) above the grid, not only inside it. */}
          <div className="ez-calendar__input">
            <input
              type="date"
              aria-label="Date"
              data-testid="date-input"
              value={value?.date ?? ''}
              onChange={(event) =>
                event.target.value === '' ? onCommit(null) : commitDate(event.target.value)
              }
              onKeyDown={(event) => event.stopPropagation()}
            />
          </div>
          <div className="ez-calendar__grid" role="grid" aria-label="Days">
            {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((label, index) => (
              <span key={`${label}${index}`} className="ez-calendar__weekday">
                {label}
              </span>
            ))}
            {daysOfMonth(cursor.year, cursor.month).map((day, index) =>
              day === null ? (
                <span key={`pad${index}`} />
              ) : (
                <button
                  key={day}
                  type="button"
                  className="ez-calendar__day"
                  data-testid={`calendar-day-${iso(day)}`}
                  data-selected={value?.date === iso(day) || undefined}
                  onClick={() => pick(day)}
                >
                  {day}
                </button>
              ),
            )}
          </div>
          <label className="ez-calendar__time">
            <input
              type="checkbox"
              data-testid="date-add-time"
              checked={withTime}
              onChange={(e) => setWithTime(e.target.checked)}
            />
            Add time
            {withTime ? (
              <input
                type="time"
                aria-label="Time"
                data-testid="date-time"
                value={time}
                onChange={(event) => setTime(event.target.value)}
                onKeyDown={(event) => event.stopPropagation()}
              />
            ) : null}
          </label>
          <button
            type="button"
            className="ez-calendar__clear"
            data-testid="date-clear"
            onClick={() => onCommit(null)}
          >
            Clear
          </button>
        </div>
      </CellPopover>
    );
  },

  SettingsDialog: DateSettingsDialog,

  fromClipboard(text, settings) {
    const trimmed = text.trim();
    if (trimmed === '') return null;
    return shared.parseValue(trimmed, settings) as DateValue | null;
  },
  toClipboard(value, settings) {
    return shared.toText(value, settings) ?? '';
  },
  bucketsOf: sharedBuckets('date'),
});
